import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";
import { LayoutDashboard, ClipboardList, HardHat, FileBarChart, Wrench, LogOut, Plus, User, CalendarClock, Users as UsersIcon, UserCog, Package } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/os", label: "Ordens de Serviço", icon: ClipboardList },
  { to: "/minhas-os", label: "Por Técnico", icon: UserCog },
  { to: "/preventiva", label: "Preventiva", icon: CalendarClock },
  { to: "/equipamentos", label: "Equipamentos", icon: Wrench },
  { to: "/almoxarifado", label: "Almoxarifado", icon: Package },
  { to: "/relatorios", label: "Relatórios", icon: FileBarChart },
  { to: "/usuarios", label: "Usuários", icon: UsersIcon, supervisorOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const navItems = NAV.filter((n) => !n.supervisorOnly || ["supervisor", "almoxarife"].includes(user?.role));

  const location = useLocation();
  const [counts, setCounts] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/sectors"), api.get("/equipments")]).then(([s, e]) => {
      setCounts(s.data.map((sec) => ({ name: sec.name, count: e.data.filter((x) => x.sector === sec.name).length })));
    }).catch(() => {});
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r bg-card" data-testid="sidebar">
        <div className="h-16 flex items-center gap-3 px-5 border-b">
          <div className="w-9 h-9 rounded-sm bg-orange-500 flex items-center justify-center">
            <HardHat className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground leading-none">Sistema</div>
            <div className="font-heading font-black text-base leading-tight">PCM Mineração</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-orange-500 text-white"
                    : "text-foreground hover:bg-secondary"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2 px-1">Setores Monitorados</div>
          <div className="space-y-1 px-1 text-xs text-muted-foreground">
            {counts.map((c) => (
              <div key={c.name} className="flex items-center justify-between"><span>{c.name}</span><span className="font-mono-tag" data-testid={`sector-count-${c.name.toLowerCase().replace(/\s/g, "-")}`}>{c.count} EQ</span></div>
            ))}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b bg-card flex items-center justify-between px-4 md:px-6 sticky top-0 z-10">
          <div className="md:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-orange-500 flex items-center justify-center">
              <HardHat className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-black">PCM</span>
          </div>
          <div className="hidden md:block text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
            Planejamento e Controle de Manutenção — Britagem & Moagem
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => nav("/os/nova")}
              data-testid="quick-add-os"
              className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold gap-2"
            >
              <Plus className="w-4 h-4" /> Nova OS
            </Button>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-sm gap-2" data-testid="user-menu">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase mt-1 text-orange-500 font-semibold">{user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Mobile nav */}
        <nav className="md:hidden flex overflow-x-auto border-b bg-card px-2 py-2 gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium whitespace-nowrap ${
                  isActive ? "bg-orange-500 text-white" : "bg-secondary text-foreground"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 p-4 md:p-8 bg-background">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
